/**
 * Floating AI Assistant — visible on every authenticated page.
 * - Bottom-right pill button with sparkle icon
 * - Click → expands into a chat panel
 * - User picks a file from library OR uploads, then chats
 * - Quick actions: summarize / translate / extract
 */
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { api, downloadFile, formatApiErrorDetail } from "../lib/api";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import RobotIcon from "./RobotIcon";
import { toast } from "sonner";

// Hide assistant on these routes (login/register/public sign)
const HIDDEN_ROUTES = ["/login", "/register"];

export default function AiAssistant() {
  const { t, i18n } = useTranslation();
  const isES = i18n.language === "es";
  const { user } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [files, setFiles] = useState([]);
  const [fileId, setFileId] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [sessionId, setSessionId] = useState(null);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);
  const fileInputRef = useRef(null);

  const hidden =
    !user ||
    HIDDEN_ROUTES.some((p) => location.pathname.startsWith(p)) ||
    location.pathname.startsWith("/sign/");

  useEffect(() => {
    if (!open || !user) return;
    api.get("/files").then(({ data }) => setFiles(data)).catch(() => {});
  }, [open, user]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, open]);

  // Reset session when file changes
  useEffect(() => {
    setSessionId(null);
    setMessages([]);
  }, [fileId]);

  if (hidden) return null;

  const currentFile = files.find((f) => f.id === fileId);

  async function uploadAndPick(file) {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/files/upload", fd);
      setFiles((prev) => [data, ...prev]);
      setFileId(data.id);
      setPickerOpen(false);
      toast.success(t("file_loaded"));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setBusy(false);
  }

  async function send(messageText) {
    const text = (messageText ?? input).trim();
    if (!text || !fileId) {
      if (!fileId) toast.error(isES ? "Selecciona un archivo primero" : "Pick a file first");
      return;
    }
    setBusy(true);
    if (!messageText) setInput("");
    setMessages((m) => [...m, { role: "user", content: text }]);
    try {
      const { data } = await api.post("/ai/chat", {
        file_id: fileId,
        message: text,
        session_id: sessionId,
      });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply }]);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setMessages((m) => m.slice(0, -1));
    }
    setBusy(false);
  }

  async function quickAction(action) {
    if (!fileId) {
      toast.error(isES ? "Selecciona un archivo primero" : "Pick a file first");
      return;
    }
    setBusy(true);
    try {
      if (action === "summarize") {
        const { data } = await api.post("/ai/summarize", {
          file_id: fileId,
          language: i18n.language,
        });
        setMessages((m) => [
          ...m,
          { role: "user", content: isES ? "✨ Resume el documento" : "✨ Summarize the document" },
          { role: "assistant", content: (isES ? "Resumen listo: " : "Summary ready: ") + data.original_filename, file: data },
        ]);
      } else if (action === "extract") {
        const { data } = await api.post("/ai/extract", { file_id: fileId });
        setMessages((m) => [
          ...m,
          { role: "user", content: isES ? "📊 Extrae los datos clave" : "📊 Extract key data" },
          { role: "assistant", content: "```json\n" + JSON.stringify(data.entities, null, 2) + "\n```" },
        ]);
      } else if (action === "translate") {
        const target = isES ? "English" : "Spanish";
        const { data } = await api.post("/ai/translate", { file_id: fileId, target_language: target });
        setMessages((m) => [
          ...m,
          { role: "user", content: (isES ? "🌍 Traduce a " : "🌍 Translate to ") + target },
          { role: "assistant", content: (isES ? "Traducción lista: " : "Translation ready: ") + data.original_filename, file: data },
        ]);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
    setBusy(false);
  }

  return (
    <>
      {/* Floating launcher */}
      <button
        onClick={() => setOpen((o) => !o)}
        className={`fixed bottom-24 right-6 z-50 grid place-items-center transition-all hover:-translate-x-[2px] hover:-translate-y-[2px] ${open ? "opacity-0 pointer-events-none" : "opacity-100"}`}
        style={{
          width: 64,
          height: 64,
          borderRadius: 0,
          background: "#3E63DD",
          border: "2px solid #1C1917",
          boxShadow: "5px 5px 0 0 #1C1917",
        }}
        aria-label="AI Assistant"
        data-testid="ai-fab"
      >
        <RobotIcon size={28} stroke="white" strokeWidth={2.2} fill="white" />
        <span className="absolute -top-2 -right-2 bg-[#FFC53D] text-[10px] font-black px-1.5 py-0.5 text-[#1C1917] border-2 border-[#1C1917] tracking-wider">
          AI
        </span>
      </button>

      {/* Backdrop */}
      {open && (
        <div
          onClick={() => setOpen(false)}
          className="fixed inset-0 z-40 bg-[#1C1917]/15 backdrop-blur-[2px] transition-opacity"
          aria-hidden
        />
      )}

      {/* Panel */}
      <div
        className={`fixed z-50 flex flex-col bg-[#FDFBF7] transition-all duration-300 origin-bottom-right ${open ? "scale-100 opacity-100" : "scale-95 opacity-0 pointer-events-none"}`}
        style={{
          right: 24,
          bottom: 96,
          width: "min(420px, calc(100vw - 32px))",
          height: "min(640px, calc(100vh - 140px))",
          border: "2px solid #1C1917",
          boxShadow: "8px 8px 0 0 #1C1917",
          overflow: "hidden",
        }}
        data-testid="ai-assistant-panel"
      >
        {/* Header */}
        <div
          className="px-5 py-4 grain border-b-2 border-[#1C1917]"
          style={{ background: "#3E63DD" }}
        >
          <div className="flex items-center justify-between">
            <div>
              <span className="eyebrow text-white/80">AI Assistant</span>
              <div className="text-white display text-xl mt-0.5">
                {isES ? "Pregunta lo que sea" : "Ask anything"}
              </div>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="text-white/80 hover:text-white text-xl w-8 h-8 grid place-items-center"
              aria-label="Close"
              data-testid="ai-panel-close"
            >
              ✕
            </button>
          </div>
        </div>

        {/* File picker bar */}
        <div className="px-4 py-3 border-b border-zinc-200 bg-zinc-50">
          <div className="flex items-center gap-2">
            <span className="eyebrow shrink-0">{isES ? "Archivo" : "File"}</span>
            <button
              onClick={() => setPickerOpen((o) => !o)}
              className="flex-1 text-left text-sm border border-zinc-300 bg-white rounded-sm px-3 h-9 truncate hover:border-black"
              data-testid="ai-panel-file-toggle"
            >
              {currentFile ? `${currentFile.original_filename}` : (isES ? "— elegir —" : "— pick one —")}
            </button>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="text-xs uppercase tracking-wider px-3 h-9 bg-black text-white rounded-sm hover:bg-zinc-800"
              data-testid="ai-panel-upload"
            >
              ↑ {isES ? "Subir" : "Upload"}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) uploadAndPick(f);
                e.target.value = "";
              }}
              data-testid="ai-panel-file-input"
            />
          </div>

          {pickerOpen && (
            <div className="mt-2 max-h-56 overflow-auto border border-zinc-200 rounded-sm bg-white" data-testid="ai-panel-file-list">
              {files.length === 0 && (
                <div className="px-3 py-4 text-xs text-zinc-500 text-center">
                  {isES ? "Aún no tienes archivos. Sube uno ↑" : "No files yet. Upload one ↑"}
                </div>
              )}
              {files.map((f) => (
                <button
                  key={f.id}
                  onClick={() => { setFileId(f.id); setPickerOpen(false); }}
                  className={`w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-zinc-50 ${fileId === f.id ? "bg-zinc-100" : ""}`}
                  data-testid={`ai-panel-file-${f.id}`}
                >
                  <span className="mono text-[10px] uppercase font-bold tracking-wider text-zinc-400 w-12 shrink-0">
                    .{f.ext}
                  </span>
                  <span className="truncate flex-1">{f.original_filename}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Quick actions */}
        <div className="flex border-b border-zinc-200 bg-white">
          {[
            { key: "summarize", label_es: "✨ Resumir", label_en: "✨ Summarize" },
            { key: "translate", label_es: "🌍 Traducir", label_en: "🌍 Translate" },
            { key: "extract", label_es: "📊 Extraer", label_en: "📊 Extract" },
          ].map((a) => (
            <button
              key={a.key}
              onClick={() => quickAction(a.key)}
              disabled={!fileId || busy}
              className="flex-1 px-2 py-2 text-xs font-medium tracking-wide text-zinc-700 hover:bg-zinc-50 disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid={`ai-quick-${a.key}`}
            >
              {isES ? a.label_es : a.label_en}
            </button>
          ))}
        </div>

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-auto px-4 py-4 bg-zinc-50/50 space-y-3" data-testid="ai-panel-messages">
          {messages.length === 0 && (
            <div className="text-center text-[#57534E] mt-8 px-4">
              <div className="inline-flex p-4 border-2 border-[#1C1917] bg-[#3E63DD] shadow-[4px_4px_0_0_#1C1917]">
                <RobotIcon size={56} stroke="white" strokeWidth={2.2} fill="#FFC53D" />
              </div>
              <p className="mt-5 text-sm font-light">
                {fileId
                  ? isES
                    ? "Pregunta lo que quieras sobre este documento."
                    : "Ask anything about this document."
                  : isES
                    ? "Selecciona un archivo (PDF, Word, Excel, PPT, HTML…) y empieza."
                    : "Pick a file (PDF, Word, Excel, PPT, HTML…) and start."}
              </p>
              {!fileId && (
                <button
                  onClick={() => setPickerOpen(true)}
                  className="mt-4 text-xs uppercase tracking-wider underline underline-offset-4 text-[#3E63DD] hover:text-[#2B4CBF] font-bold"
                  data-testid="ai-empty-pickfile"
                >
                  {isES ? "Elegir archivo →" : "Pick a file →"}
                </button>
              )}
            </div>
          )}
          {messages.map((m, i) => (
            <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
              <div
                className={`max-w-[85%] px-3 py-2.5 rounded-md text-sm whitespace-pre-wrap leading-relaxed ${
                  m.role === "user"
                    ? "bg-black text-white"
                    : "bg-white border border-zinc-200"
                }`}
              >
                {m.content}
                {m.file && (
                  <button
                    onClick={() => downloadFile(m.file.id, m.file.original_filename)}
                    className="block mt-2 text-xs underline hover:no-underline"
                    data-testid={`ai-msg-download-${m.file.id}`}
                  >
                    ↓ {t("cta_download")}
                  </button>
                )}
              </div>
            </div>
          ))}
          {busy && (
            <div className="flex justify-start">
              <div className="bg-white border border-zinc-200 rounded-md px-3 py-2 text-xs text-zinc-500" data-testid="ai-thinking">
                {t("ai_thinking")}
              </div>
            </div>
          )}
        </div>

        {/* Input */}
        <div className="px-3 py-3 border-t border-zinc-200 bg-white flex gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !busy) send(); }}
            placeholder={t("ai_chat_placeholder")}
            className="rounded-sm border-zinc-300 h-10 text-sm"
            data-testid="ai-panel-input"
          />
          <Button
            onClick={() => send()}
            disabled={busy || !input.trim() || !fileId}
            className="rounded-sm bg-black text-white hover:bg-zinc-800 h-10 px-4"
            data-testid="ai-panel-send"
          >
            →
          </Button>
        </div>
      </div>
    </>
  );
}
